import { defineStore } from 'pinia'
import { ref } from 'vue'
import { fetchKnowledgeOverview } from '@/services/knowledge.service'
import { listPrompts } from '@/services/quality.service'
import { KNOWLEDGE_SECTIONS } from '@/config/knowledgeSections'

/**
 * Per-section DONE state — the single lock the Business Brain gates on
 * (Jens 2026-08-18: a section page is reachable ONLY when its row shows the
 * green checkmark; menu and direct addresses both enforce it).
 *
 * Done = the same rule the Overview paints: the section exists, the engine's
 * verdict is good (stale counts as good), and no question is open on it.
 * The verdict itself lives in ONE place server-side
 * (contextEngine/staleness.statusFor) — this store only reads it.
 */
export const useSectionStatusStore = defineStore('sectionStatus', () => {
  const doneBySlug = ref<Record<string, boolean>>({})
  const strategyReady = ref(false)
  const loaded = ref(false)
  const loadedWorkspaceId = ref<string | null>(null)

  async function load(workspaceId: string | number, opts: { force?: boolean } = {}) {
    const wid = String(workspaceId)
    if (!opts.force && loaded.value && loadedWorkspaceId.value === wid) return
    try {
      const [overview, promptList] = await Promise.all([
        fetchKnowledgeOverview(wid),
        listPrompts(wid, { surface: 'dashboard', limit: 50 }).catch(() => ({ prompts: [] })),
      ])
      const statusByType: Record<string, string> = {}
      for (const e of overview.entities) statusByType[e.type] = e.status
      const openTypes = new Set((promptList.prompts || []).map(p => p.targetEntityType))
      const map: Record<string, boolean> = {}
      for (const section of KNOWLEDGE_SECTIONS) {
        if (!section.slug) continue
        const status = statusByType[section.type]
        map[section.slug] = (status === 'good' || status === 'stale') && !openTypes.has(section.type)
      }
      doneBySlug.value = map
      strategyReady.value = overview.strategyReady === true
      loadedWorkspaceId.value = wid
      loaded.value = true
    } catch (e) {
      console.error('[sectionStatus] load failed:', e)
      // On failure keep whatever we had — the gates fail CLOSED for
      // never-loaded workspaces (isDone returns false until data exists).
    }
  }

  /** Slugs that are not one of the 17 sections (overview, strategy) are
   *  never gated. */
  function isGatedSlug(slug: string): boolean {
    return KNOWLEDGE_SECTIONS.some(s => s.slug === slug)
  }

  function isDone(slug: string): boolean {
    if (!isGatedSlug(slug)) return true
    return doneBySlug.value[slug] === true
  }

  return { doneBySlug, strategyReady, loaded, load, isDone, isGatedSlug }
})
